import { CreditCardOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Modal } from 'antd';
import { setAuthorizationBase } from 'config/axiosBase';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authClientSignIn } from 'service/base';
import { CONST_SITE, LocalStorageKey } from 'utils/common/const';
import { dispatchAuthChanged } from 'utils/common/authEvents';

function pickAuthToken(res) {
  const d = res?.data;
  if (typeof d === 'string' && d.length > 20) return d;
  if (d && typeof d === 'object') {
    return d.userToken || d.access_token || d.accessToken || d.token || d.jwt || null;
  }
  return res?.userToken || res?.access_token || res?.accessToken || res?.token || null;
}

function pickErrorMessage(err, fallback) {
  const data = err?.response?.data;
  return (
    data?.message || data?.text_mess || data?.title_mess || data?.error || err?.message || fallback
  );
}

const LoginModal = ({ open, onClose, registerHref = '#', onLoggedIn }) => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      const saved = localStorage.getItem('userAccount');
      if (saved) {
        form.setFieldsValue({ username: saved });
      }
    }
  }, [open, form]);

  const handleClose = () => {
    if (!loading) onClose?.();
  };

  const onFinish = async (values) => {
    const username = (values.username || '').trim();
    const lastbank = String(values.lastbank || '')
      .replace(/\D/g, '')
      .slice(0, 4);

    if (!username) return;
    if (lastbank.length !== 4) {
      notification.warning({
        message: 'Thiếu thông tin',
        description: 'Vui lòng nhập đúng 4 số cuối tài khoản ngân hàng.'
      });
      return;
    }

    setLoading(true);
    try {
      const res = await authClientSignIn({
        username,
        lastbank,
        site: String(CONST_SITE.C168 || 'c168').trim()
      });

      const token = pickAuthToken(res);
      const ok = res?.code === 0 || res?.code === 200 || res?.success === true || Boolean(token);

      if (ok && token) {
        const clean = String(token).replace(/"/g, '');
        localStorage.setItem(LocalStorageKey.ACCESS_TOKEN, clean);
        const refresh = res?.data?.refresh_token || res?.data?.refreshToken || res?.refresh_token;
        if (refresh) {
          localStorage.setItem(LocalStorageKey.REFRESH_TOKEN, String(refresh).replace(/"/g, ''));
        }
        const user = res?.data?.user ?? res?.data?.userInfo;
        if (user && typeof user === 'object') {
          localStorage.setItem(LocalStorageKey.USER_INFO, JSON.stringify(user));
        }
        localStorage.setItem('userAccount', username);
        setAuthorizationBase(clean);
        dispatchAuthChanged();
        notification.success({
          message: 'Thành công',
          description: `Xin chào, ${username}!`
        });
        onLoggedIn?.();
        onClose?.();
        form.resetFields();
        return;
      }

      notification.error({
        message: 'Xác nhận thất bại',
        description: res?.message || res?.text_mess || 'Thông tin không khớp. Vui lòng kiểm tra lại.'
      });
    } catch (err) {
      notification.error({
        message: 'Xác nhận thất bại',
        description: pickErrorMessage(err, 'Không thể kết nối máy chủ. Vui lòng thử lại.')
      });
    } finally {
      setLoading(false);
    }
  };

  const registerIsValid = Boolean(registerHref && registerHref !== '#');
  const registerIsExternal = registerIsValid && /^https?:\/\//i.test(String(registerHref));

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      maskClosable={!loading}
      closable
      title={null}
      width={340}
      className="login-modal-c168"
      styles={{
        content: {
          padding: 0,
          borderRadius: 16,
          overflow: 'hidden',
          maxWidth: 'min(340px, calc(100vw - 24px))',
          width: '100%',
          margin: '0 auto'
        },
        body: { padding: 0 }
      }}
    >
      <div
        className="px-4 pt-5 pb-4 sm:px-5 sm:pt-6 sm:pb-5"
        style={{
          background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 45%)'
        }}
      >
        <div className="text-center mb-4">
          <h2 className="text-base font-black text-[#1f2937] tracking-tight m-0">Kiểm tra thông tin</h2>
          <p className="text-[11px] sm:text-xs text-[#6b7280] mt-1 mb-0 leading-snug px-1">
            Tên tài khoản và 4 số cuối TK ngân hàng liên kết
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          className="login-form-c168"
          size="middle"
        >
          <Form.Item
            name="username"
            label={<span className="text-[#374151] font-semibold text-xs">Tên tài khoản</span>}
            rules={[
              { required: true, message: 'Vui lòng nhập tên tài khoản.' },
              { min: 2, message: 'Tên tài khoản quá ngắn' }
            ]}
          >
            <Input
              prefix={<UserOutlined className="text-[#9ca3af]" />}
              placeholder="Nhập username..."
              autoComplete="username"
              className="rounded-lg !py-1.5 text-sm"
              maxLength={128}
            />
          </Form.Item>

          <Form.Item
            name="lastbank"
            label={
              <span className="text-[#374151] font-semibold text-xs leading-snug">
                4 số cuối tài khoản liên kết ngân hàng
              </span>
            }
            rules={[
              { required: true, message: 'Vui lòng nhập 4 số cuối.' },
              { pattern: /^\d{4}$/, message: 'Vui lòng nhập đúng 4 số.' }
            ]}
            getValueFromEvent={(e) =>
              String(e?.target?.value ?? '')
                .replace(/\D/g, '')
                .slice(0, 4)
            }
          >
            <Input
              prefix={<CreditCardOutlined className="text-[#9ca3af]" />}
              placeholder="Nhập 4 số cuối ngân hàng..."
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              className="rounded-lg !py-1.5 text-sm"
            />
          </Form.Item>

          <Form.Item className="mb-2 mt-5">
            <div className="flex items-center justify-end gap-2">
              <Button
                type="default"
                onClick={handleClose}
                disabled={loading}
                className="rounded-full px-4 h-9 font-semibold text-[#374151]"
              >
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="rounded-full px-5 h-9 font-extrabold text-sm border-0 shadow-md min-w-[100px]"
                style={{
                  background: 'linear-gradient(180deg, #3a86ff 0%, #0053df 100%)'
                }}
              >
                Xác nhận
              </Button>
            </div>
          </Form.Item>
        </Form>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1 sm:gap-2 text-center text-xs pt-1">
          <span className="text-[#6b7280]">Chưa có tài khoản?</span>
          {registerIsValid ? (
            registerIsExternal ? (
              <a
                href={registerHref}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#0053df] hover:text-[#003ea8] no-underline"
              >
                Đăng ký ngay
              </a>
            ) : (
              <Link
                to={registerHref}
                className="font-bold text-[#0053df] hover:text-[#003ea8]"
                onClick={handleClose}
              >
                Đăng ký ngay
              </Link>
            )
          ) : (
            <span className="font-semibold text-[#9ca3af]">Đăng ký (đang cập nhật)</span>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LoginModal;
